import React from "react";
import { ClockIcon } from "../Auth/onboarding/icons";
import { ChatBubbleIcon, UsersIcon, TrendUpIcon, CalendarIcon } from "./icons";
import {
  formatNumber,
  formatPercent,
  formatSeconds,
  formatChange,
} from "./mapOverview";

const CheckBubbleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path
      d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7A2.5 2.5 0 0 1 17.5 16H10l-4.5 3.5V16h-1A2.5 2.5 0 0 1 2 13.5v-7"
      strokeLinejoin="round"
    />
    <path d="M8.5 9.8l1.8 1.8L15 7.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// The window the API says these numbers cover. Formatted from the UTC parts:
// "from" arrives as midnight UTC, and reading it in a timezone west of GMT
// would print the day before.
const utcDay = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString(undefined, {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const coveredRange = (overview) => {
  const from = utcDay(overview?.from);
  const to = utcDay(overview?.to);

  if (!from || !to) return "";
  // A single-day window reads better as one date than as "x - x".
  return from === to ? from : `${from} \u2013 ${to}`;
};

export default function StatCards({ overview, comparedTo }) {
  const covered = coveredRange(overview);

  const stats = [
    {
      icon: <ChatBubbleIcon />,
      variant: "indigo",
      label: "Total conversations",
      value: formatNumber(overview?.totalConversations?.value),
      change: overview?.totalConversations?.changePct,
    },
    {
      icon: <UsersIcon />,
      variant: "green",
      label: "Leads captured",
      value: formatNumber(overview?.leads?.value),
      change: overview?.leads?.changePct,
    },
    {
      icon: <CheckBubbleIcon />,
      variant: "amber",
      label: "Resolution rate",
      value: formatPercent(overview?.resolutionRate?.value),
      change: overview?.resolutionRate?.changePct,
    },
    {
      icon: <ClockIcon />,
      variant: "blue",
      label: "Avg. response time",
      value: formatSeconds(overview?.avgResponseTime?.value),
      change: overview?.avgResponseTime?.changePct,
    },
  ];

  return (
    <>
      <div className="dash-stats">
        {stats.map((stat) => {
          const delta = formatChange(stat.change);
          const down = stat.change < 0;
          // Straight off the sign: down is red, up is green. Flat stays
          // neutral, since nothing moved and neither colour would be true.
          const tone = !stat.change
            ? " dash-stat-delta--flat"
            : down
              ? " dash-stat-delta--bad"
              : "";

          return (
            <div className="dash-stat-card" key={stat.label}>
              <span className={`dash-stat-icon dash-stat-icon--${stat.variant}`}>{stat.icon}</span>
              <span className="dash-stat-label">{stat.label}</span>
              <span className="dash-stat-value">{stat.value}</span>
              {delta && (
                <span
                  className={`dash-stat-delta${down ? " dash-stat-delta--down" : ""}${tone}`}
                >
                  <TrendUpIcon />
                  {delta}
                  {comparedTo && <span className="dash-stat-compare">vs {comparedTo}</span>}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
